import { ReactNode, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Heart,
  Settings,
  FileText,
  Activity,
  CheckCircle,
  LogOut,
  Menu,
  X,
  MessageCircle,
} from "lucide-react";
import { clsx } from "clsx";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "../hooks/useApi";
import { isToday } from "date-fns";
import { ChatBubble } from "./ChatBubble";
import { ServiceWorkerUpdate } from "./ServiceWorkerUpdate";
import { VERSION_DISPLAY } from "../version";

interface LayoutProps {
  children: ReactNode;
  onLogout?: () => void;
}

export function Layout({ children, onLogout }: LayoutProps) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const apiClient = useApi();

  // Fetch user status to determine check-in state for heart animation
  const { data: status } = useQuery({
    queryKey: ["status"],
    queryFn: () => apiClient.getStatus(),
    refetchInterval: 30000, // Refresh every 30 seconds
    enabled: !!apiClient, // Only run if apiClient is available
  });

  const hasCheckedInToday =
    status && status.lastCheckin ? isToday(new Date(status.lastCheckin)) : false;

  const navigation = [
    { name: "Chat", href: "/", icon: MessageCircle },
    { name: "Journey", href: "/journey", icon: Activity },
    { name: "Check In", href: "/checkin", icon: CheckCircle },
    { name: "Settings", href: "/settings", icon: Settings },
    { name: "Audit Log", href: "/audit", icon: FileText },
  ];

  return (
    <div className="min-h-screen bg-gray-50 relative">
      {/* Beating Heart Watermark */}
      <div className="fixed -bottom-[18.33vh] -right-[18.33vh] w-[65vh] h-[50vh] pointer-events-none z-0 overflow-visible">
        {/* Main heartbeat with particles */}
        <div className="relative w-[55vh] h-[55vh]">
          {/* Ripple particle hearts */}
          {hasCheckedInToday && (
            <>
              <Heart
                className="absolute w-[55vh] h-[55vh] transform rotate-12 text-red-400 opacity-0 animate-heartbeat-ripple origin-bottom-right"
                style={{ left: 0, top: 0 }}
              />
            </>
          )}

          {/* Main heart */}
          <Heart
            className={clsx(
              "absolute w-[55vh] h-[55vh] transform rotate-12 origin-bottom-right",
              hasCheckedInToday
                ? "text-red-500 opacity-10 animate-heartbeat"
                : "text-gray-400 opacity-5"
            )}
          />
        </div>
      </div>

      <nav className="bg-white shadow-sm border-b border-gray-200 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <Link
                to="/"
                className="flex-shrink-0 flex items-center hover:opacity-80 transition-opacity"
              >
                <Heart className="h-8 w-8 text-red-600" />
                <span className="ml-2 text-xl font-bold text-gray-900">mAlive</span>
              </Link>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={clsx(
                        "inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium",
                        location.pathname === item.href
                          ? "border-red-500 text-gray-900"
                          : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                      )}
                    >
                      <Icon className="h-4 w-4 mr-2" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>
              <div className="flex items-center">
              {/* Mobile menu button */}
              <div className="sm:hidden">
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="inline-flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500"
                >
                  {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </div>
              {/* Desktop logout button */}
              {onLogout && (
                <div className="hidden sm:flex sm:items-center sm:ml-6">
                <button
                  onClick={onLogout}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="sm:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-200">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={clsx(
                      "block px-3 py-2 rounded-md text-base font-medium",
                      location.pathname === item.href
                        ? "bg-red-50 text-red-700 border-red-500"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    )}
                  >
                    <div className="flex items-center">
                      <Icon className="h-5 w-5 mr-3" />
                      {item.name}
                    </div>
                  </Link>
                );
              })}
              {onLogout && (
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onLogout();
                  }}
                  className="w-full text-left block px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                >
                  <div className="flex items-center">
                    <LogOut className="h-5 w-5 mr-3" />
                    Logout
                  </div>
                </button>
              )}
            </div>
          </div>
        )}
      </nav>

      <main className="max-w-7xl mx-auto py-4 sm:py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-4 sm:py-6 sm:px-0">{children}</div>
      </main>

      {/* Chat Bubble - appears on all screens except Chat page */}
      {location.pathname !== "/" && <ChatBubble />}

      {/* Service Worker Update Notifications */}
      <ServiceWorkerUpdate />

      {/* Footer */}
      <footer className="mt-16 py-4 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-xs text-gray-400">
            <span className="font-mono">{VERSION_DISPLAY}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
